import type {
  CalculatorDefinition,
  CalculatorInputValue,
  CalculatorInputValues,
  CalculatorResult,
  InputDefinition,
} from './types';

export class CalculatorValidationError extends Error {
  readonly inputId: string;

  constructor(inputId: string, message: string) {
    super(message);
    this.name = 'CalculatorValidationError';
    this.inputId = inputId;
  }
}

function isEmpty(value: CalculatorInputValue | undefined): boolean {
  return value === undefined || value === '';
}

function validateInput(
  input: InputDefinition,
  value: CalculatorInputValue | undefined,
): void {
  const required = input.required ?? true;

  if (isEmpty(value)) {
    if (required) {
      throw new CalculatorValidationError(input.id, `${input.label} 값이 필요합니다.`);
    }
    return;
  }

  if (input.type === 'select') {
    if (typeof value !== 'string') {
      throw new CalculatorValidationError(input.id, `${input.label} 선택값이 올바르지 않습니다.`);
    }

    if (input.options && !input.options.some((option) => option.value === value)) {
      throw new CalculatorValidationError(input.id, `${input.label} 선택값이 허용 범위를 벗어났습니다.`);
    }
    return;
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new CalculatorValidationError(input.id, `${input.label}에는 유효한 숫자를 입력해야 합니다.`);
  }

  if (input.min !== undefined && value < input.min) {
    throw new CalculatorValidationError(input.id, `${input.label} 값은 ${input.min} 이상이어야 합니다.`);
  }

  if (input.max !== undefined && value > input.max) {
    throw new CalculatorValidationError(input.id, `${input.label} 값은 ${input.max} 이하여야 합니다.`);
  }
}

export function runCalculator(
  definition: CalculatorDefinition,
  values: CalculatorInputValues,
): CalculatorResult {
  for (const input of definition.inputs) {
    validateInput(input, values[input.id]);
  }

  return definition.calculate(values);
}
